@import './colors.css';
@import 'bootstrap/scss/bootstrap-utilities.scss';
@import './telerik.scss';

:root {
    --red-hat: 'Red Hat Text', sans-serif;
    --red-hat-mono: 'Red Hat Mono', monospace;
    --monitor-status-clear: rgba(0, 0, 0, 0);
    --monitor-status-info: rgb(131, 185, 210);
    --monitor-status-watch: rgb(255, 255, 128);
    --monitor-status-warning: rgb(255, 191, 0);
    --monitor-status-critical: rgb(255, 112, 112);
    --monitor-status-emergency: rgb(255, 32, 223);
    --info-blue: rgb(0, 120, 250);
    --fe-primary: var(--color-purple-500);
    --fe-primary-50: rgba(112, 77, 255, 50%);
    --nav-selected-bg-color: light-dark(var(--fe-primary), white);
    --nav-bg-color: light-dark(rgba(112, 77, 255, 0.3), var(--color-neutral-300));
    --color-neutral-900: #f7f7f7;
    --flight-departed: #6699ff;
    --flight-in-flight: #00e5e5;
    --flight-arrived: #5a5a5a;
    --flight-aged-out: #5a5a5a;
    --flight-hold: rgba(0, 0, 0, 0);
    --flight-planned: #00dc00;
    --flight-scheduled: #ebebc8;
    --flight-ground: #3030f0;
    --blue-05: #040b2b;
    --secondary-text-color: light-dark(var(--color-neutral-500), var(--color-neutral-600));
    color-scheme: var(--scheme);
    --stroke-focus: #2969f2;
    --font-clock: 'Digital Font', monospace;

    /* Bootstrap overrides */
    --bs-border-color: light-dark(var(--color-neutral-700), var(--color-neutral-300));
    --bg-color: light-dark(var(--color-neutral-900), var(--color-neutral-050));

    --bs-purple: var(--color-purple-400);
    --bs-red: var(--color-semantic-red);
    --bs-yellow: var(--color-semantic-yellow);
    --bs-green: var(--color-semantic-green);
}

[data-is-dark='false'] {
    --scheme: light;
    --tooltip-bg-color: var(--color-neutral-900);
    --highlighted-bg-color: var(--color-neutral-800);
    --text-color: black;
    --text-inverse: white;
    --border-color: var(--color-neutral-700);
    --alternate-color: var(--color-neutral-900);
}

[data-is-dark='true'] {
    --scheme: dark;
    --tooltip-bg-color: var(--color-neutral-200);
    --highlighted-bg-color: var(--color-neutral-400);
    --text-color: white;
    --text-inverse: black;
    --border-color: var(--color-neutral-300);
    --alternate-color: var(--color-neutral-100);
}

@font-face {
    font-family: 'Digital Font';
    src: url('/fonts/digital.ttf');
    font-weight: normal;
}

html {
    overflow-x: hidden;
    overflow-y: hidden;
}

html,
body {
    margin: 0;
    font-family: var(--red-hat);
}

button {
    font-family: var(--red-hat);
}

iframe {
    background-color: var(--bg-color);
}

.code {
    font-family: var(--red-hat-mono);
}

.font-mono {
    font-family: var(--red-hat-mono);
}

.font-clock {
    line-height: 0.9;
    font-size: 1.7rem;
    font-family: var(--font-clock);
}

.danger-text {
    color: var(--color-red-400);
}

/* bg */
.monstat-bg-clear {
    background-color: var(--monitor-status-clear);
    color: black;
}

.monstat-bg-info {
    background-color: var(--monitor-status-info);
    color: black;
}

.monstat-bg-watch {
    background-color: var(--monitor-status-watch);
    color: black;
}

.monstat-bg-warning {
    background-color: var(--monitor-status-warning);
    color: black;
}

.monstat-bg-critical {
    background-color: var(--monitor-status-critical);
    color: black;
}

.monstat-bg-emergency {
    background-color: var(--monitor-status-emergency);
    color: black;
}

/* badge */
.monstat-badge-clear {
    background-color: var(--monitor-status-clear);
    color: white;
}

.monstat-badge-info {
    background-color: var(--monitor-status-info);
    color: white;
}

.monstat-badge-watch {
    background-color: var(--monitor-status-watch);
    color: black;
}

.monstat-badge-warning {
    background-color: var(--monitor-status-warning);
    color: white;
}

.monstat-badge-critical {
    background-color: var(--monitor-status-critical);
    color: white;
}

.monstat-badge-emergency {
    background-color: var(--monitor-status-emergency);
    color: white;
}

.monstat-badge {
    border-radius: 4px;
    padding-left: 0.25rem;
    padding-right: 0.25rem;
}

/* color */
.monstat-txt-clear {
    color: light-dark(black, white) !important;
}

.monstat-txt-info {
    color: var(--monitor-status-info) !important;
}

.monstat-txt-watch {
    color: var(--monitor-status-watch) !important;
    text-shadow: 1px 1px 4px var(--color-neutral-500);
}

[data-is-dark='true'] .monstat-txt-watch {
    text-shadow: none;
}

.monstat-txt-warning {
    color: var(--monitor-status-warning) !important;
}

.monstat-txt-critical {
    color: var(--monitor-status-critical) !important;
}

.monstat-txt-emergency {
    color: var(--monitor-status-emergency) !important;
}

.secondary-text {
    font-size: 0.8rem;
    color: var(--color-neutral-500);
}

[data-is-dark='true'] .secondary-text {
    color: var(--color-neutral-700);
}

.secondary-text-color {
    color: var(--secondary-text-color);
}

.secondary-bg-color {
    background-color: light-dark(var(--color-neutral-900), var(--color-neutral-100));
}

.k-expander-header {
    background-color: light-dark(var(--color-neutral-900), var(--color-neutral-100));
}

.k-expander-title {
    color: var(--text-color);
}

.k-expander {
    padding-top: 0;
    margin-top: 0;
}
.k-expander + .k-expander.k-expanded,
.k-expander.k-expanded + .k-expander {
    padding-top: 0;
    margin-top: 0;
}

.k-expander-content {
    padding-block: 0;
    padding-inline: 0;
}
.mark,
mark {
    background-color: yellow;
    padding: 0;
}

html,
body,
#app {
    height: 100%;
}

h1:focus {
    outline: none;
}

a,
.btn-link {
    color: #0071c1;
}

.btn-primary {
    color: #fff;
    background-color: #1b6ec2;
    border-color: #1861ac;
}

.section-header {
    background-color: light-dark(var(--color-neutral-800), var(--color-neutral-100));
    height: 34px;
}

.btn:focus,
.btn:active:focus,
.btn-link.nav-link:focus,
.form-control:focus,
.form-check-input:focus {
    box-shadow:
        0 0 0 0.1rem white,
        0 0 0 0.25rem #258cfb;
}

.valid.modified:not([type='checkbox']) {
    outline: 1px solid #26b050;
}

.invalid {
    outline: 1px solid red;
}

.validation-message {
    color: red;
}

body {
    color: var(--text-color);
    background-color: var(--bg-color);
}

.cursor-pointer {
    cursor: pointer;
}

.cursor-not-allowed {
    cursor: not-allowed;
}

marker {
    background-color: yellow;
    color: black;
}

.border-left {
    border-left: 1px solid var(--bs-border-color);
}

.border-right {
    border-right: 1px solid var(--bs-border-color);
}

.border-primary {
    border-color: var(--fe-primary) !important;
}

.bg-color {
    background-color: var(--bg-color);
}

.k-table-tbody > tr:nth-child(2n) {
    background-color: light-dark(var(--color-neutral-900), var(--color-neutral-100)) !important;
}

.navigation-ctrl-container .maplibregl-ctrl > button {
    height: 30px !important;
    width: 25px !important;
}

.maplibregl-ctrl-top-left .maplibregl-ctrl {
    margin: 0.5rem 0 0 0.5rem;
}

[data-is-dark='true'] .maplibregl-ctrl-group {
    background-color: #212121 !important;
}

[data-is-dark='true'] .maplibregl-ctrl button.maplibregl-ctrl-compass .maplibregl-ctrl-icon {
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='29' height='29' fill='%23fff' viewBox='0 0 29 29'%3E%3Cpath d='m10.5 14 4-8 4 8h-8z'/%3E%3Cpath fill='%23999' d='m10.5 16 4 8 4-8h-8z'/%3E%3C/svg%3E") !important;
}

[data-is-dark='true'] .maplibregl-ctrl button.maplibregl-ctrl-terrain .maplibregl-ctrl-icon {
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='19' height='19' fill='%23fff' viewBox='0 0 22 22'%3E%3Cpath d='m1.754 13.406 4.453-4.851 3.09 3.09 3.281 3.277.969-.969-3.309-3.312 3.844-4.121 6.148 6.886h1.082v-.855l-7.207-8.07-4.84 5.187L6.169 6.57l-5.48 5.965v.871ZM.688 16.844h20.625v1.375H.688Zm0 0'/%3E%3C/svg%3E") !important;
}

.maplibregl-ctrl button.maplibregl-ctrl-terrain-enabled .maplibregl-ctrl-icon {
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='19' height='19' fill='%236699FF' viewBox='0 0 22 22'%3E%3Cpath d='m1.754 13.406 4.453-4.851 3.09 3.09 3.281 3.277.969-.969-3.309-3.312 3.844-4.121 6.148 6.886h1.082v-.855l-7.207-8.07-4.84 5.187L6.169 6.57l-5.48 5.965v.871ZM.688 16.844h20.625v1.375H.688Zm0 0'/%3E%3C/svg%3E") !important;
}

.maplibregl-ctrl-top-left {
    display: flex;
    z-index: 0 !important;
}

.maplibregl-ctrl button.maplibregl-ctrl-terrain .maplibregl-ctrl-icon {
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='19' height='19' fill='%23333' viewBox='0 0 22 22'%3E%3Cpath d='m1.754 13.406 4.453-4.851 3.09 3.09 3.281 3.277.969-.969-3.309-3.312 3.844-4.121 6.148 6.886h1.082v-.855l-7.207-8.07-4.84 5.187L6.169 6.57l-5.48 5.965v.871ZM.688 16.844h20.625v1.375H.688Zm0 0'/%3E%3C/svg%3E") !important;
}

[data-is-dark='true'] .ctrl-background {
    background-color: #212121 !important;
}

.maplibregl-ctrl-bottom-left,
.maplibregl-ctrl-bottom-right,
.maplibregl-ctrl-top-left,
.maplibregl-ctrl-top-right {
    z-index: 0 !important;
}

.navigation-ctrl {
    padding-right: 0 !important;
}

.fe-navigation-dropdown {
    width: 20px !important;
    height: 36px !important;
    border: none !important;
    cursor: pointer;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url('data:image/svg+xml,<svg xmlns:xlink="http://www.w3.org/1999/xlink" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M256 352 128 160h256z"></path></svg>');
}

[data-is-dark='true'] .fe-navigation-dropdown {
    background-image: url('data:image/svg+xml,<svg xmlns:xlink="http://www.w3.org/1999/xlink" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill=\'%23fff\' d=\'M256 352 128 160h256z\'></path></svg>');
}

.fe-navigation-dropdown:hover {
    background-color: rgb(0 0 0 / 5%);
}

[data-is-dark='true'] .fe-navigation-dropdown:hover {
    background-color: rgb(255 255 255 / 5%);
}

.maplibregl-ctrl-compass {
    width: 24px !important;
    height: 24px !important;
}

.maplibregl-ctrl-group {
    box-shadow: none !important;
}

.maplibregl-ctrl-icon {
    width: 20px !important;
    height: 20px !important;
}

.max-h-100 {
    max-height: 100% !important;
}

select {
    background-color: whitesmoke;
    border-width: 0;
    border-right: 5px solid transparent;
    outline: 1px solid rgba(0, 0, 0, 0.38);
    border-radius: 4px;
    height: 30px;
    padding-inline-start: 0.125rem;
}

[data-is-dark='true'] select {
    background-color: #252525;
}

/* Small telerik button */
.button-small.k-button-md.k-icon-button {
    min-width: 18px !important;
    min-height: 18px !important;
    padding: 0 !important;
}

.button-small.k-button {
    min-width: 20px !important;
    /*min-height: 20px !important;*/
    padding: 3px !important;
}
.button-small.k-button.avatar-button {
    min-width: 18px !important;
    min-height: 18px !important;
    padding: 0 !important;
}

/*.transparent-popup.k-child-animation-container.k-popup {*/
/*    --bs-bg-opacity: 1;*/
/*    background-color: transparent!important;*/
/*}*/

.text-break-anywhere {
    line-break: anywhere;
}

.button-no-upper.k-button {
    text-transform: none !important;
}

[data-is-dark='true'] .k-slider .k-slider-track {
    /* Telerik's version is just black which doesn't provide sufficient contrast */
    background-color: var(--color-neutral-300) !important;
}

.small-gap-end.k-dropdownlist .k-input-inner {
    padding-inline-end: 0 !important;
}

.k-tabstrip-content:focus,
.k-tabstrip-content.k-focus,
.k-tabstrip > .k-content:focus,
.k-tabstrip > .k-content.k-focus {
    outline: none !important;
}

.k-tabstrip-items-wrapper {
    max-width: 365px;
}

.k-input-inner {
    text-transform: inherit !important;
}

table.cobalt-table {
    font-size: 14px;
}

table.cobalt-table thead > tr {
    font-size: 12px;
    border-top: 0.125rem solid transparent;
    border-bottom: 0.125rem solid transparent;
}

table.cobalt-table thead > tr > th {
    font-weight: 400;
}

table.cobalt-table:not(.cobalt-compact) tbody > tr {
    border-top: 0.375rem solid transparent;
    border-bottom: 0.375rem solid transparent;
}

table.cobalt-table td,
table.cobalt-table th {
    padding-left: 0.125rem;
    padding-right: 0.5rem;
}

table.cobalt-table tbody > tr > th,
td {
    padding-right: 0.125rem;
    padding-left: 0.5rem;
}

table.cobalt-table.cobalt-alternate tbody > tr:nth-child(2n) {
    background-color: var(--alternate-color);
}

table.cobalt-table.cobalt-alternate tbody > tr:nth-child(2n) > .alternate-bg-color {
    background-color: var(--alternate-color);
}

.content-flex.k-tabstrip > .k-tabstrip-content.k-content.k-active {
    display: flex !important;
    flex-direction: column;
    min-height: 0; /* flex grow hack */
}

.hide-tabstrip > .k-tabstrip-items-wrapper {
    display: none !important;
}

.hide-tabstrip-content .k-tabstrip-content {
    display: none !important;
}

.card-body-flex {
    display: flex;
    flex-direction: column;
    min-height: 0;
    max-height: 100%;
}

.popup-horizontal-fix {
    transform: translate(calc(-100% + 54px));
}

.text-blue {
    color: var(--fe-primary);
}

.text-success {
    color: var(--color-green-400);
}

.text-warning {
    color: var(--color-yellow-400);
}

.text-error {
    color: var(--color-red-400) !important;
}

.card-warning {
    background-color: light-dark(var(--color-yellow-900), var(--color-yellow-050));
    border-color: light-dark(var(--color-yellow-600), var(--color-yellow-400));
}

.k-menu-button.k-button-outline-base {
    border-color: rgba(0, 0, 0, 0.38);
    gap: 0.125rem;
    padding-left: 12px;
}

[data-is-dark='true'] .k-menu-button.k-button-outline-base {
    border-color: #49535a;
    background-color: #31383c !important;
}

.z-1 {
    z-index: 1 !important;
}

.z-2 {
    z-index: 2 !important;
}

.z-3 {
    z-index: 3 !important;
}

.z-4 {
    z-index: 4 !important;
}

.z-5 {
    z-index: 5 !important;
}

.k-popup.fe-round {
    border-radius: 4px !important;
}

input[type='range'] {
    color: red;
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    cursor: pointer;
    width: 15rem;
    --track-color: #b2c9fa;
}

[data-is-dark='true'] input[type='range'] {
    --track-color: var(--color-neutral-300);
}

/***** Chrome, Safari, Opera, and Edge Chromium *****/
input[type='range']::-webkit-slider-runnable-track {
    background: var(--track-color);
    height: 2px;
}

/******** Firefox ********/
input[type='range']::-moz-range-track {
    background: var(--track-color);
    height: 2px;
}

/***** Chrome, Safari, Opera, and Edge Chromium *****/
input[type='range']::-webkit-slider-thumb {
    -webkit-appearance: none; /* Override default look */
    appearance: none;
    margin-top: -6.5px; /* Centers thumb on the track */
    background-color: var(--fe-primary);
    height: 14px;
    width: 14px;
    border-color: var(--fe-primary);
    border-radius: 50%;
}

/***** Firefox *****/
input[type='range']::-moz-range-thumb {
    appearance: none;
    margin-top: -7px; /* Centers thumb on the track */
    background-color: var(--fe-primary);
    border-width: 0;
    height: 14px;
    width: 14px;
    border-color: var(--fe-primary);
    border-radius: 50%;
}

/* Removes default focus */
input[type='range']:focus {
    outline: none;
}

/***** Chrome, Safari, Opera, and Edge Chromium *****/
input[type='range']:focus::-webkit-slider-thumb {
    outline: 8px solid rgba(0, 120, 250, 0.25);
    outline-offset: 0;
}

/******** Firefox ********/
input[type='range']:focus::-moz-range-thumb {
    outline: 8px solid rgba(0, 120, 250, 0.25);
    outline-offset: 0;
}

.input-uppercase > .k-input-inner {
    text-transform: uppercase !important;
}

.input-uppercase > .k-input-inner::placeholder {
    text-transform: none !important;
}

.input-transparent {
    user-drag: none;
    -webkit-user-drag: none;
    user-select: none;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
}

.rvr-1 {
    color: black;
    background-color: #00ff00;
}

.rvr-2 {
    color: white;
    background-color: var(--info-blue);
}

.rvr-3 {
    color: black;
    background-color: #ffff00;
}

.rvr-4 {
    background-color: #ff8000;
}

.rvr-5 {
    background-color: #ff0000;
}

.rvr-6 {
    background-color: #ff00ff;
}

.taf-bars-border {
    border: 1px solid black;
    background-color: white;
}

[data-is-dark='true'] .taf-bars-border {
    border: 1px solid var(--border-color);
    background-color: var(--color-neutral-100);
}

/* Help button */

.help-btn {
    width: 27px;
    height: 27px;
    border-radius: 16px;
    background-color: white !important;
}

[data-is-dark='true'] .help-btn {
    background-color: var(--color-neutral-200) !important;
}

.help-btn > .k-menu-item {
    width: 100%;
    height: 100%;
}

.help-btn > .k-menu-item:hover {
    background: none !important;
}

.help-btn .k-menu-link {
    padding-left: 0;
    padding-right: 0;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    gap: 0;
}

.help-btn .k-svg-icon {
    width: 20px;
    height: 20px;
}

.help-btn .k-menu-link-text {
    display: none;
}

.help-btn .k-menu-expand-arrow {
    display: none;
}

.airport-link {
    color: var(--text-color);
    text-decoration: underline;
}

/* Chrome, Safari... */
.flight-details-scrollbars::-webkit-scrollbar-track {
    background-color: transparent;
}

.flight-details-scrollbars::-webkit-scrollbar {
    width: 6px; /* Set the width of the scrollbar */
}

.flight-details-scrollbars::-webkit-scrollbar-thumb {
    background-color: var(--border-color);
    border-radius: 6px; /* Optional: round the corners of the thumb */
}

/* Firefox */
.flight-details-scrollbars {
    scrollbar-color: var(--border-color) transparent; /* thumb color and track color */
    scrollbar-width: thin; /* Optional: make the scrollbar thinner */
}

.icon-b-1 {
    position: relative;
    bottom: 1px;
}

.icon-b-2 {
    position: relative;
    bottom: 2px;
}

.icon-t-1 {
    position: relative;
    top: 1px;
}

.icon-t-2 {
    position: relative;
    top: 2px;
}

.maplibregl-ctrl-bottom-left {
    padding-bottom: 54px;
}

.maplibregl-ctrl.maplibregl-ctrl-attrib {
    margin: 0 !important;
    border-radius: 0 12px 12px 0 !important;
    background-color: #fff7 !important;
    padding: 2px 0 0 22px !important;
}

.maplibregl-ctrl.maplibregl-ctrl-attrib.maplibregl-compact-show {
    padding: 2px 8px 0 22px !important;
}

.maplibregl-ctrl-attrib-button {
    width: 20px !important;
    height: 20px !important;
    bottom: 0 !important;
    top: auto !important;
    background-color: transparent !important;
    background-position: center !important;
}

.dropdown-popup-disable-hover .k-list-item {
    padding: 0 !important;
    cursor: default !important;
}

.dropdown-popup-disable-hover .k-list-item:hover {
    background-color: inherit !important;
}

.map-style-dropdown-horizontal-fix {
    transform: translate(-97px, 0);
}

.weather-bg {
    background-color: var(--color-neutral-900);
}

[data-is-dark='true'] .weather-bg {
    background-color: var(--color-neutral-100);
}

.clickable:hover {
    cursor: pointer;
    background-color: light-dark(var(--color-neutral-800), var(--color-neutral-200));
}

.selected-light-bg {
    background-color: light-dark(var(--color-neutral-700), var(--color-neutral-300)) !important;
}

.k-slider-selection {
    z-index: 1;
}

.k-radio-item,
.k-radio-list-item {
    padding-block: 0 !important;
}

.font-400 {
    font-weight: 400 !important;
}

.line-through-disabled-checked .k-radio-item:has(input:disabled:checked) label {
    text-decoration: line-through !important;
}

.font-weight-bold {
    font-weight: bold;
}

.dropdown-button-small {
    min-width: 18px;
    min-height: 18px;
    padding: 0 !important;
    gap: 0 !important;
}

.dropdown-button-small .k-button-arrow {
    display: none !important;
}

.dropdown-button-small .k-icon {
    height: 17px !important;
    width: 17px !important;
}

.fe-hover:hover {
    border-radius: 4px;
    outline: 2px solid var(--fe-primary);
    outline-offset: -2px;
}

.fe-dropdown-small.k-dropdownlist {
    height: 29px;
}

.fe-dropdown-small.k-dropdownlist .k-input-inner {
    font-size: 14px;
    padding-inline-start: 0.5em;
    padding-inline-end: 0.5em;
}

.mx-m3 {
    margin-left: -0.75rem !important;
    margin-right: -0.75rem !important;
}

/*Small Telerik Grid*/
.fe-small-grid.k-grid td,
.k-grid td.k-command-cell {
    line-height: 1;
}

.fe-small-grid.k-grid td.k-command-cell .k-button {
    margin: 0;
    padding-top: 0;
    padding-bottom: 0;
    line-height: 1;
    height: auto;
}

.fe-small-grid.k-grid .k-grid-header .k-grid-header-wrap .k-grid-header-table .k-table-thead .k-table-row .k-header {
    padding-top: 0;
    padding-bottom: 0;
}

.fe-small-grid.k-grid .k-grid-header .k-grid-header-wrap .k-grid-header-table .k-table-thead .k-table-row .k-header .k-grid-header-menu {
    padding-top: 0;
    padding-bottom: 0;
    line-height: 1;
    height: min-content;
    bottom: calc(4px + 0.285715em) !important; /*there is similar value in material-main-dark.css but it's wrong for multi-column headers*/
}

.fe-small-grid.compact-column th {
    padding: 0 !important;
    padding-left: 0.2rem !important;
}

.fe-small-grid.compact-column th:first-child {
    padding: 0 !important;
    padding-left: 0.8rem !important;
}

.fe-small-grid.compact-column td:first-child {
    padding: 0 !important;
    padding-left: 0.8rem !important;
}

.k-grid-p-0.k-grid td {
    padding-block: 0 !important;
    padding-inline: 0 !important;
}

.k-grid-no-header.k-grid .k-grid-header {
    display: none !important;
}

.k-grid-no-borders.k-grid {
    border: none !important;
}

/* */
.telerik-cobaltify.k-grid {
    border: none !important;
}

.telerik-cobaltify .k-grid-column-menu {
    opacity: 0;
    transition: opacity 100ms linear;
}
.telerik-cobaltify .k-grid-header {
    border-bottom: 0 !important;
}

.telerik-cobaltify th:hover .k-grid-column-menu {
    opacity: 1;
}

.telerik-cobaltify thead tr th,
.telerik-cobaltify tbody tr td {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    line-height: 1.2 !important;
    border: none !important;
}

.telerik-cobaltify thead tr th {
    border-bottom: 2px solid light-dark(black, white) !important;
}

.telerik-cobaltify tbody tr:hover {
    background-color: light-dark(var(--color-neutral-800), var(--color-neutral-200)) !important;
}

.telerik-cobaltify td:focus {
    background-color: unset !important;
}

.telerik-cobaltify .k-table-thead {
    color: var(--text-color) !important;
}

.telerik-cobaltify thead tr th {
    font-size: 0.7rem;
    color: var(--text-color);
    align-items: center;
    height: 22px;
    font-weight: 600 !important;
}

.telerik-cobaltify thead tr th:focus {
    background-color: unset !important;
    color: unset !important;
}

.telerik-cobaltify thead tr th .k-cell-inner {
    height: 100%;
    flex-grow: 0 !important;
    margin-block: 0 !important;
    margin-inline: 0 !important;
}

.telerik-cobaltify thead tr th .k-cell-inner .k-grid-column-menu {
    padding: 0 !important;
    height: 17px !important;
    width: 17px !important;
}
.telerik-cobaltify thead tr th .k-cell-inner .k-icon,
.telerik-cobaltify thead tr th .k-cell-inner .k-sort-icon {
    height: 10px !important;
    width: 10px !important;
}

.telerik-cobaltify thead tr th .k-cell-inner .k-sort-icon .k-sort-order {
    color: unset !important;
    font-size: 0.7em !important;
    margin: 0 !important;
    margin-inline-start: 0 !important;
}

.telerik-cobaltify thead tr th .k-cell-inner .k-link {
    padding: 0 !important;
}

.telerik-cobaltify .k-sort-icon,
.telerik-cobaltify .k-sorted {
    background-color: unset !important;
    color: var(--text-color) !important;
}

.telerik-cobaltify colgroup .k-sorted {
    background-color: unset !important;
}

.monitor-status-cell {
    border-radius: 4px;
}

/* */
.threshold-popup-translate {
    transform: translate(-275px, 0);
}

.airport-threshold-popup-translate {
    transform: translate(-480px, 0);
}

.scheduled-displayer-container {
    background-color: var(--tooltip-bg-color);
    position: absolute;
    z-index: 999;
    padding: 0.5rem;
    padding-top: 0;
    font-size: 0.8rem;
    width: fit-content;
    height: fit-content;
    text-align: left;
    vertical-align: middle;
    white-space: nowrap;
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 0.1rem;
    max-height: 200px;
    overflow-y: auto;
    border-radius: 0.2rem;
}

.scheduled-displayer-header {
    margin-left: -0.5rem;
    width: calc(100% + 1rem);
    padding: 0.5rem;
    text-align: center;
    position: sticky;
    top: 0;
    z-index: 1000;
}

.scheduled-displayer-row {
    display: grid;
    width: 13rem;
    grid-template-columns: 0.4fr 0.4fr 0.2fr;
    column-gap: 0.2rem;
    cursor: pointer;
}

.scheduled-displayer-row:hover {
    background-color: var(--highlighted-bg-color);
}

.distance-bearing-presenter {
    background-color: var(--tooltip-bg-color);
    z-index: 888;
    padding: 0.2rem;
    font-size: 0.8rem;
    width: fit-content;
    height: fit-content;
    text-align: center;
    vertical-align: middle;
    white-space: nowrap;
    border-radius: 0.2rem;
}

.measure-displayer {
    background-color: var(--tooltip-bg-color);
    position: absolute;
    z-index: 999;
    padding: 0.2rem;
    font-size: 0.8rem;
    width: fit-content;
    height: fit-content;
    text-align: center;
    vertical-align: middle;
    white-space: nowrap;
    border-radius: 0.2rem;
}

.maplibregl-canvas:focus-visible {
    outline: none;
}

.grid-hide-filter-row .k-filter-row {
    display: none;
}

.text-opacity-50 {
    color: light-dark(rgba(0, 0, 0, 0.5), rgba(255, 255, 255, 0.5));
}

.non-hover .k-chip {
    pointer-events: none;
}

.non-hover .k-chip-action {
    pointer-events: auto;
}

.filter-group-chips .k-chip-label {
    font-size: 10px;
}

.filter-group-chips .k-input-inner {
    font-size: 12px;
}

.filter-group-clear {
    font-size: 12px;
}

.border-outline {
    border: 1px solid rgba(0, 0, 0, 0.38);
    border-radius: 4px;
}

.note-content p {
    margin-block-start: 0;
    margin-block-end: 0;
}

.k-editor-toolbar {
    z-index: 1000;
}

.k-editor-content p {
    margin: 0 !important;
}

.k-card .k-card-avatar,
.k-card .k-avatar {
    width: 32px !important;
    height: 32px !important;
    flex-basis: 32px !important;
    margin-right: 8px !important;
}

.k-menu-link-me-0 .k-menu-link {
    padding-inline-end: 1rem !important;
}

.telerik-button-group {
    display: flex;
    border-radius: 4px;
}

.group-radius {
    border-radius: 4px;
}

.telerik-button-group > * {
    width: 27px;
    height: 27px;
    box-shadow: none;
}

.telerik-button-group > *:first-child {
    border-end-end-radius: 0;
    border-start-end-radius: 0;
}

.telerik-button-group > *:not(:last-child):not(:first-child) {
    border-start-start-radius: 0;
    border-start-end-radius: 0;
    border-end-start-radius: 0;
    border-end-end-radius: 0;
}

.telerik-button-group > *:last-child {
    border-start-start-radius: 0;
    border-end-start-radius: 0;
}

.telerik-popup-transparent {
    background: none !important;
    border: none !important;
    box-shadow: none !important;
}

.clickable[aria-disabled='true'] {
    color: rgba(0, 0, 0, 0.5);
}

.clickable:hover[aria-disabled='true'] {
    background: none;
    cursor: default;
}

.clickable-item {
    display: grid;
    grid-template-columns: 24px 1fr;
    align-items: center;
}

.note-window > .k-window-content {
    overflow: auto !important;
    padding: 0 !important;
    display: flex;
}

.note-window > .k-window-titlebar {
    background-color: light-dark(var(--color-neutral-900), var(--color-neutral-100));
    padding: 0 10px 0 10px !important;
}

.k-slider.m-0 .k-slider-track-wrap {
    margin-right: 0 !important;
    margin-left: 0 !important;
}

.k-slider.custom-track .k-slider-track {
    height: 2px;
    z-index: 0;
    background-color: var(--border-color) !important;
}

.k-slider.custom-track .k-slider-selection {
    background: none !important;
}

.k-slider.rectangle-draghandle-v .k-draghandle {
    background-color: rgba(0, 120, 250, 0.4) !important;
    border-radius: 0 !important;
    border: 1px solid light-dark(#212121, white);
    width: 10px;
    height: 18px;
    box-sizing: content-box;
    transform: translate(calc(-50% + 0px), calc(-50% + 2px)) !important;
}
.k-slider.rectangle-draghandle-v .k-draghandle::after {
    display: none;
}

.k-slider.no-focus .k-draghandle:focus {
    box-shadow: none !important;
}

.map-tools-bg-color {
    background-color: light-dark(#fff, #212121) !important;
}

.route-analyzer-button-group {
    border-left: 1px solid var(--border-color);
    border-right: 1px solid var(--border-color);
}

.route-analyzer-button-group > .k-button:not(:last-child) {
    height: 28px;
    width: 28px;
    border-radius: 0;
    border-right: 1px solid var(--border-color) !important;
}

#add-route-btn {
    box-shadow: none;
    height: 28px;
    border-radius: 0;
    border-left: 1px solid var(--border-color);
    border-right: 1px solid var(--border-color);
}

#add-route-btn > button:first-child {
    aspect-ratio: 1/1;
    border-right: 1px solid var(--border-color) !important;
}

#add-route-btn > button:nth-child(2) {
    width: 20px;
    padding: 0 !important;
}

#route-analyzer-list tbody tr td {
    height: 10px;
}
/* Util */
.min-h-0 {
    min-height: 0;
}

.max-h-100 {
    max-height: 100%;
}

.min-w-0 {
    min-width: 0;
}

.max-w-100 {
    max-width: 100%;
}

.fe-badge {
    font-size: 0.75em;
    line-height: 1.37;
    color: white;
    border-radius: 4px;
    padding: 0 0.7em;
    background-color: var(--fe-primary);
}

.fe-badge-secondary {
    font-size: 0.75em;
    line-height: 1.5;
    color: black;
    border-radius: 4px;
    padding: 0 0.7em;
    background-color: light-dark(var(--color-neutral-800), var(--color-neutral-600));
}

.fe-badge-company {
    font-size: 0.75em;
    line-height: 1.5;
    color: black;
    border-radius: 4px;
    padding: 0 0.7em;
    background-color: light-dark(var(--color-neutral-700), var(--color-neutral-500));
}

.fe-badge-outline {
    font-size: 0.75em;
    line-height: 1.5;
    color: var(--text-color);
    border-radius: 4px;
    padding: 0 0.7em;
    border: 1px solid var(--border-color);
    background-color: transparent;
}

.k-menu-popup .k-menu-item:has(.dropdown-item-border-bottom) {
    border-bottom: 1px solid var(--border-color);
}

.k-button > .k-icon {
    pointer-events: none;
}

.k-tooltip-wrapper > div {
    height: 100%;
}
.notes-details-tab {
    display: flex !important;
    flex-direction: row;
    height: 100%;
    max-height: 100% !important;
    max-width: 500px;
}
.notes-details-tab > div {
    max-width: 500px;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
}

.app-title {
    font-family: 'Red Hat Display', sans-serif;
    font-size: 1.2rem;
    color: light-dark(black, white);
    text-decoration: none !important;
}

.app-title:hover {
    color: light-dark(black, white);
}

.primary-bg-color {
    background-color: light-dark(white, var(--color-neutral-075));
}

.k-expander-header {
    padding-block: 4px;
    padding-inline: 8px;
}

.profile-canvas-background {
    background-color: light-dark(white, var(--color-neutral-050)) !important;
    position: absolute;
    top: 0;
    left: 0;
}

.profile-canvas {
    background-color: transparent !important;
    position: absolute;
    top: 0;
    left: 0;
}

.error-icon-bg {
    border-radius: 8px;
    background-color: light-dark(var(--color-neutral-800), var(--color-neutral-600));
}

.vertical-button-group {
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
    border-radius: 4px;
    overflow: hidden;
    border-width: var(--kendo-button-border-width, 1px);
    border-style: solid;
    border-color: var(--kendo-input-border);
}

.vertical-button-group .k-button {
    border-width: 0 !important;
    border-radius: 0 !important;
}

.alternate-bg-color {
    background-color: var(--alternate-color);
}

.custom-grid-row:hover {
    background-color: light-dark(var(--color-neutral-800), var(--color-neutral-200)) !important;
}

.scrollbar-gutter-stable {
    scrollbar-gutter: stable;
}

/* luma shader compilation error window (it's bugged and doesn't scroll. This is fixed in newer version of luma so we can remove it when we upgrade) */
body > button {
    height: 100vh;
    overflow: scroll;
}

.d-contents {
    display: contents;
}
